import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import RoleBasedNavigation from '../../components/navigation/RoleBasedNavigation';
import BreadcrumbTrail from '../../components/navigation/BreadcrumbTrail';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { supabase } from '../../lib/supabase';
import BatchUpdateModal from './components/BatchUpdateModal';
import BatchAudioUploadModal from './components/BatchAudioUploadModal';

const EditPlay = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, userProfile, isAuthenticated } = useAuth();
  const [playId, setPlayId] = useState(null);
  const [playData, setPlayData] = useState(null);
  const [lines, setLines] = useState([]);
  const [filteredLines, setFilteredLines] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterAct, setFilterAct] = useState('1');
  const [filterScene, setFilterScene] = useState('all');
  const [selectedLines, setSelectedLines] = useState([]);
  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);
  const [isBatchAudioModalOpen, setIsBatchAudioModalOpen] = useState(false);
  const [filterAudioOnly, setFilterAudioOnly] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filterGid, setFilterGid] = useState('');
  const [filterActFrom, setFilterActFrom] = useState('');
  const [filterActTo, setFilterActTo] = useState('');
  const [filterSceneFrom, setFilterSceneFrom] = useState('');
  const [filterSceneTo, setFilterSceneTo] = useState('');
  const [filterLineInScene, setFilterLineInScene] = useState('');

  useEffect(() => {
    if (location?.state?.playId) {
      setPlayId(location?.state?.playId);
    }
  }, [location]);

  useEffect(() => {
    if (playId && isAuthenticated) {
      fetchPlayData();
      fetchLines();
    }
  }, [playId, isAuthenticated]);

  useEffect(() => {
    applyFilters();
  }, [lines, searchQuery, filterAct, filterScene, filterAudioOnly, filterGid, filterActFrom, filterActTo, filterSceneFrom, filterSceneTo, filterLineInScene]);

  useEffect(() => {
    applySorting();
  }, [sortConfig]);

  const fetchPlayData = async () => {
    try {
      const { data, error } = await supabase
        ?.from('plays')
        ?.select('*')
        ?.eq('id', playId)
        ?.single();

      if (error) throw error;

      // Get character count
      const { data: charactersData } = await supabase
        ?.from('lines')
        ?.select('character')
        ?.eq('play_id', playId)
        ?.not('character', 'is', null);

      const uniqueCharacters = new Set(charactersData?.map(line => line?.character));

      // Get active student count
      const { count: studentCount } = await supabase
        ?.from('user_progress')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('play_id', playId);

      setPlayData({
        ...data,
        characterCount: uniqueCharacters?.size || 0,
        studentCount: studentCount || 0
      });
    } catch (err) {
      console.error('Error fetching play data:', err);
      setError('Failed to load play details');
    }
  };

  const fetchLines = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        ?.from('lines')
        ?.select(`
          *,
          scenes(act_number, scene_number, title)
        `)
        ?.eq('play_id', playId)
        ?.order('gid', { ascending: true })
        ?.limit(10000); // Explicitly set high limit to override default 1000 row limit

      if (error) throw error;

      // Fetch all audio recordings for this play in bulk
      const lineIds = data?.map(line => line?.id);
      const { data: audioData } = await supabase
        ?.from('audio_recordings')
        ?.select('line_id')
        ?.in('line_id', lineIds);

      // Fetch all glossary assignments for this play in bulk
      const { data: glossaryData } = await supabase
        ?.from('line_glossary_assignments')
        ?.select('line_id')
        ?.in('line_id', lineIds);

      // Create lookup maps for efficient checking
      const audioLineIds = new Set(audioData?.map(a => a?.line_id) || []);
      const glossaryCountMap = new Map();
      glossaryData?.forEach(g => {
        glossaryCountMap?.set(g?.line_id, (glossaryCountMap?.get(g?.line_id) || 0) + 1);
      });

      // Enrich lines with audio and glossary status
      const linesWithAudio = data?.map((line) => ({
        ...line,
        hasAudio: audioLineIds?.has(line?.id),
        glossaryTermCount: glossaryCountMap?.get(line?.id) || 0,
        actNumber: line?.scenes?.act_number,
        sceneNumber: line?.scenes?.scene_number
      }));

      setLines(linesWithAudio);
      setFilteredLines(linesWithAudio);
    } catch (err) {
      console.error('Error fetching lines:', err);
      setError('Failed to load play lines');
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = lines;

    if (searchQuery) {
      filtered = filtered?.filter(line =>
        line?.dialogue?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        line?.character?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    // GID lookup (supports partial match, e.g. "42" matches gid 42 or zero-padded "0042")
    if (filterGid?.trim()) {
      const gidNum = parseInt(filterGid?.trim(), 10);
      if (!isNaN(gidNum)) {
        filtered = filtered?.filter(line => line?.gid === gidNum);
      } else {
        // string partial match on zero-padded display
        const padded = filterGid?.trim()?.padStart(4, '0');
        filtered = filtered?.filter(line =>
          String(line?.gid)?.padStart(4, '0')?.includes(padded)
        );
      }
    }

    // Act range filter (overrides single act filter when range is set)
    const actFromNum = filterActFrom !== '' ? parseInt(filterActFrom, 10) : null;
    const actToNum = filterActTo !== '' ? parseInt(filterActTo, 10) : null;
    if (actFromNum !== null || actToNum !== null) {
      filtered = filtered?.filter(line => {
        const act = line?.actNumber;
        if (actFromNum !== null && act < actFromNum) return false;
        if (actToNum !== null && act > actToNum) return false;
        return true;
      });
    } else if (filterAct !== 'all') {
      filtered = filtered?.filter(line => line?.actNumber === parseInt(filterAct));
    }

    // Scene range filter (overrides single scene filter when range is set)
    const sceneFromNum = filterSceneFrom !== '' ? parseInt(filterSceneFrom, 10) : null;
    const sceneToNum = filterSceneTo !== '' ? parseInt(filterSceneTo, 10) : null;
    if (sceneFromNum !== null || sceneToNum !== null) {
      filtered = filtered?.filter(line => {
        const scene = line?.sceneNumber;
        if (sceneFromNum !== null && scene < sceneFromNum) return false;
        if (sceneToNum !== null && scene > sceneToNum) return false;
        return true;
      });
    } else if (filterScene !== 'all') {
      filtered = filtered?.filter(line => line?.sceneNumber === parseInt(filterScene));
    }

    // line_in_scene exact match
    if (filterLineInScene?.trim()) {
      const lineInSceneNum = parseInt(filterLineInScene?.trim(), 10);
      if (!isNaN(lineInSceneNum)) {
        filtered = filtered?.filter(line => line?.line_in_scene === lineInSceneNum);
      }
    }

    if (filterAudioOnly) {
      filtered = filtered?.filter(line => line?.hasAudio);
    }

    setFilteredLines(filtered);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const applySorting = () => {
    if (!sortConfig?.key) return;

    const sorted = [...filteredLines]?.sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig?.key) {
        case 'lineId':
          aValue = a?.gid ?? '';
          bValue = b?.gid ?? '';
          break;
        case 'actScene':
          aValue = `${a?.actNumber}-${String(a?.sceneNumber)?.padStart(4, '0')}`;
          bValue = `${b?.actNumber}-${String(b?.sceneNumber)?.padStart(4, '0')}`;
          break;
        case 'lineInScene':
          aValue = a?.line_in_scene ?? 0;
          bValue = b?.line_in_scene ?? 0;
          break;
        case 'character':
          aValue = (a?.character || '')?.toLowerCase();
          bValue = (b?.character || '')?.toLowerCase();
          break;
        case 'dialogue':
          aValue = (a?.dialogue || '')?.toLowerCase();
          bValue = (b?.dialogue || '')?.toLowerCase();
          break;
        case 'status':
          aValue = (a?.hasAudio ? 1 : 0) + (a?.glossaryTermCount || 0);
          bValue = (b?.hasAudio ? 1 : 0) + (b?.glossaryTermCount || 0);
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortConfig?.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig?.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    setFilteredLines(sorted);
  };

  const getSortIcon = (key) => {
    if (sortConfig?.key !== key) {
      return <Icon name="ChevronsUpDown" size={14} className="text-muted-foreground" />;
    }
    return sortConfig?.direction === 'asc' 
      ? <Icon name="ChevronUp" size={14} className="text-primary" />
      : <Icon name="ChevronDown" size={14} className="text-primary" />;
  };

  const handleEditLine = (lineId) => {
    navigate('/line-editing-modal', {
      state: {
        lineId: lineId,
        playId: playId,
        playTitle: playData?.title
      }
    });
  };

  const handleBulkEdit = () => {
    if (selectedLines?.length > 0) {
      setIsBatchModalOpen(true);
    }
  };

  const handleBatchAudioUpload = () => {
    if (selectedLines?.length > 0) {
      setIsBatchAudioModalOpen(true);
    }
  };

  const handleBatchUpdateComplete = () => {
    setSelectedLines([]);
    fetchLines();
  };

  const handleBatchAudioUploadComplete = () => {
    setSelectedLines([]);
    fetchLines();
  };

  const toggleLineSelection = (lineId) => {
    setSelectedLines(prev =>
      prev?.includes(lineId)
        ? prev?.filter(id => id !== lineId)
        : [...prev, lineId]
    );
  };

  const handleCharacterManagement = () => {
    navigate('/character-management', {
      state: {
        playId: playId,
        playTitle: playData?.title
      }
    });
  };

  const handleClearLineFilters = () => {
    setFilterGid('');
    setFilterActFrom('');
    setFilterActTo('');
    setFilterSceneFrom('');
    setFilterSceneTo('');
    setFilterLineInScene('');
    setFilterAct('all');
    setFilterScene('all');
    setSearchQuery('');
  };

  const hasActiveLineFilters = filterGid || filterActFrom || filterActTo || filterSceneFrom || filterSceneTo || filterLineInScene;

  const getUniqueActNumbers = () => {
    // Use play metadata for total acts if available, otherwise fall back to lines
    if (playData?.total_acts) {
      return Array.from({ length: playData?.total_acts }, (_, i) => i + 1);
    }
    // Fallback: derive from lines if play metadata not available
    const acts = new Set(lines?.map(line => line?.actNumber));
    return Array.from(acts)?.sort((a, b) => a - b);
  };

  const getUniqueScenesForAct = () => {
    if (filterAct === 'all') return [];
    const scenes = new Set(
      lines
        ?.filter(line => line?.actNumber === parseInt(filterAct))
        ?.map(line => line?.sceneNumber)
    );
    return Array.from(scenes)?.sort((a, b) => a - b);
  };

  if (!playId) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-foreground mb-2">No Play Selected</h2>
          <p className="text-muted-foreground mb-4">Please select a play from the Play Management page</p>
          <Button onClick={() => navigate('/play-management')}>
            Go to Play Management
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedNavigation
        user={userProfile}
        isAuthenticated={isAuthenticated}
        notifications={[]}
      />
      <main className="main-content">
        <BreadcrumbTrail />

        <div className="max-w-screen-2xl mx-auto px-6 py-8">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/play-management')}
                iconName="ArrowLeft"
              >
                Back to Play Management
              </Button>
            </div>

            {playData && (
              <div className="bg-card rounded-lg border border-border p-6 mb-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h1 className="text-3xl font-bold text-card-foreground mb-2">{playData?.title}</h1>
                    <p className="text-lg text-muted-foreground">{playData?.author}</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/analytics-dashboard', { state: { playId } })}
                    iconName="BarChart3"
                  >
                    View Analytics
                  </Button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="bg-muted rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="BookOpen" size={18} className="text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Acts</span>
                    </div>
                    <p className="text-2xl font-bold text-card-foreground">{playData?.total_acts || 0}</p>
                  </div>

                  <div className="bg-muted rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="FileText" size={18} className="text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Scenes</span>
                    </div>
                    <p className="text-2xl font-bold text-card-foreground">{playData?.total_scenes || 0}</p>
                  </div>

                  <div className="bg-muted rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="MessageSquare" size={18} className="text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Lines</span>
                    </div>
                    <p className="text-2xl font-bold text-card-foreground">{playData?.total_lines || 0}</p>
                  </div>

                  <div 
                    className="bg-muted rounded-lg p-4 cursor-pointer hover:bg-muted/80 transition-colors duration-200"
                    onClick={handleCharacterManagement}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => {
                      if (e?.key === 'Enter' || e?.key === ' ') {
                        handleCharacterManagement();
                      }
                    }}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="Users2" size={18} className="text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Characters</span>
                      <Icon name="ExternalLink" size={14} className="text-muted-foreground ml-auto" />
                    </div>
                    <p className="text-2xl font-bold text-card-foreground">{playData?.characterCount || 0}</p>
                  </div>

                  <div className="bg-muted rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="GraduationCap" size={18} className="text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Active Students</span>
                    </div>
                    <p className="text-2xl font-bold text-card-foreground">{playData?.studentCount || 0}</p>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 flex items-center gap-3 mb-6">
                <Icon name="AlertCircle" size={20} className="text-destructive" />
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-card-foreground">Line Management</h2>
                {selectedLines?.length > 0 && (
                  <div className="bg-primary/10 border border-primary rounded-lg p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Icon name="CheckSquare" size={20} className="text-primary" />
                      <span className="text-sm font-medium text-foreground">
                        {selectedLines?.length} line(s) selected
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleBatchAudioUpload}
                        iconName="Upload"
                        iconPosition="left"
                      >
                        Batch Audio Upload
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleBulkEdit}
                        iconName="Edit"
                        iconPosition="left"
                      >
                        Batch Update
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedLines([])}
                        iconName="X"
                      >
                        Clear
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <Input
                  type="search"
                  placeholder="Search by line, character, or ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="md:col-span-2"
                />

                <select
                  value={filterAct}
                  onChange={(e) => {
                    setFilterAct(e?.target?.value);
                    setFilterScene('all');
                  }}
                  className="px-4 py-2 rounded-md border border-border bg-card text-card-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="all">All Acts</option>
                  {getUniqueActNumbers()?.map(act => (
                    <option key={act} value={act}>Act {act}</option>
                  ))}
                </select>

                <select
                  value={filterScene}
                  onChange={(e) => setFilterScene(e?.target?.value)}
                  disabled={filterAct === 'all'}
                  className="px-4 py-2 rounded-md border border-border bg-card text-card-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                >
                  <option value="all">All Scenes</option>
                  {getUniqueScenesForAct()?.map(scene => (
                    <option key={scene} value={scene}>Scene {scene}</option>
                  ))}
                </select>
              </div>

              {/* Advanced Line Lookup Controls */}
              <div className="bg-muted/40 border border-border rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Icon name="SlidersHorizontal" size={16} className="text-muted-foreground" />
                    <span className="text-sm font-medium text-foreground">Line Lookup</span>
                  </div>
                  {hasActiveLineFilters && (
                    <button
                      onClick={handleClearLineFilters}
                      className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
                    >
                      <Icon name="X" size={10} />
                      Clear filters
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {/* GID lookup */}
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1 font-medium">GID (e.g. 0042)</label>
                    <Input
                      type="text"
                      placeholder="e.g. 0042 or 42"
                      value={filterGid}
                      onChange={(e) => setFilterGid(e?.target?.value)}
                      className="w-full font-mono text-sm"
                    />
                  </div>

                  {/* Act range */}
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1 font-medium">Act Range</label>
                    <div className="flex items-center gap-1">
                      <Input
                        type="number"
                        placeholder="From"
                        min="1"
                        value={filterActFrom}
                        onChange={(e) => setFilterActFrom(e?.target?.value)}
                        className="w-full text-sm"
                      />
                      <span className="text-muted-foreground text-xs px-1">–</span>
                      <Input
                        type="number"
                        placeholder="To"
                        min="1"
                        value={filterActTo}
                        onChange={(e) => setFilterActTo(e?.target?.value)}
                        className="w-full text-sm"
                      />
                    </div>
                  </div>

                  {/* Scene range */}
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1 font-medium">Scene Range</label>
                    <div className="flex items-center gap-1">
                      <Input
                        type="number"
                        placeholder="From"
                        min="1"
                        value={filterSceneFrom}
                        onChange={(e) => setFilterSceneFrom(e?.target?.value)}
                        className="w-full text-sm"
                      />
                      <span className="text-muted-foreground text-xs px-1">–</span>
                      <Input
                        type="number"
                        placeholder="To"
                        min="1"
                        value={filterSceneTo}
                        onChange={(e) => setFilterSceneTo(e?.target?.value)}
                        className="w-full text-sm"
                      />
                    </div>
                  </div>

                  {/* line_in_scene */}
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1 font-medium">Line in Scene #</label>
                    <Input
                      type="number"
                      placeholder="e.g. 5"
                      min="1"
                      value={filterLineInScene}
                      onChange={(e) => setFilterLineInScene(e?.target?.value)}
                      className="w-full text-sm"
                    />
                  </div>
                </div>
                {hasActiveLineFilters && (
                  <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                    {filterGid && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs">
                        GID: {String(parseInt(filterGid, 10) || 0)?.padStart(4, '0')}
                        <button onClick={() => setFilterGid('')}><Icon name="X" size={10} /></button>
                      </span>
                    )}
                    {(filterActFrom || filterActTo) && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs">
                        Act {filterActFrom || '1'}–{filterActTo || '∞'}
                        <button onClick={() => { setFilterActFrom(''); setFilterActTo(''); }}><Icon name="X" size={10} /></button>
                      </span>
                    )}
                    {(filterSceneFrom || filterSceneTo) && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs">
                        Scene {filterSceneFrom || '1'}–{filterSceneTo || '∞'}
                        <button onClick={() => { setFilterSceneFrom(''); setFilterSceneTo(''); }}><Icon name="X" size={10} /></button>
                      </span>
                    )}
                    {filterLineInScene && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs">
                        Line in Scene: #{filterLineInScene}
                        <button onClick={() => setFilterLineInScene('')}><Icon name="X" size={10} /></button>
                      </span>
                    )}
                  </div>
                )}
              </div>

              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="hidden md:grid md:grid-cols-12 gap-4 px-4 py-2 bg-muted rounded-lg text-sm font-medium text-muted-foreground">
                    <div className="col-span-1">Select</div>
                    <div 
                      className="col-span-2 flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors"
                      onClick={() => handleSort('lineId')}
                    >
                      <span>GID</span>
                      {getSortIcon('lineId')}
                    </div>
                    <div 
                      className="col-span-2 flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors"
                      onClick={() => handleSort('actScene')}
                    >
                      <span>Act / Scene</span>
                      {getSortIcon('actScene')}
                    </div>
                    <div 
                      className="col-span-1 flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors"
                      onClick={() => handleSort('lineInScene')}
                    >
                      <span>Line#</span>
                      {getSortIcon('lineInScene')}
                    </div>
                    <div 
                      className="col-span-2 flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors"
                      onClick={() => handleSort('character')}
                    >
                      <span>Character</span>
                      {getSortIcon('character')}
                    </div>
                    <div 
                      className="col-span-2 flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors"
                      onClick={() => handleSort('dialogue')}
                    >
                      <span>Dialogue</span>
                      {getSortIcon('dialogue')}
                    </div>
                    <div 
                      className="col-span-1 flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors"
                      onClick={() => handleSort('status')}
                    >
                      <span>Status</span>
                      {getSortIcon('status')}
                    </div>
                    <div className="col-span-1">Actions</div>
                  </div>

                  {filteredLines?.map((line) => (
                    <div
                      key={line?.id}
                      className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-shadow duration-200"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
                        <div className="md:col-span-1">
                          <input
                            type="checkbox"
                            checked={selectedLines?.includes(line?.id)}
                            onChange={() => toggleLineSelection(line?.id)}
                            className="w-4 h-4 rounded border-border"
                          />
                        </div>

                        <div className="md:col-span-2">
                          <span className="text-xs text-muted-foreground md:hidden">GID: </span>
                          <span className="text-sm font-mono text-card-foreground">
                            {line?.gid != null ? String(line?.gid)?.padStart(4, '0') : (line?.line_id || 'N/A')}
                          </span>
                        </div>

                        <div className="md:col-span-2">
                          <span className="text-xs text-muted-foreground md:hidden">Location: </span>
                          <span className="text-sm text-card-foreground">
                            A{line?.actNumber} S{line?.sceneNumber}
                          </span>
                        </div>

                        <div className="md:col-span-1">
                          <span className="text-xs text-muted-foreground md:hidden">Line#: </span>
                          <span className="text-sm text-card-foreground">
                            {line?.line_in_scene != null ? (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-muted text-xs font-mono">#{line?.line_in_scene}</span>
                            ) : (
                              <span className="text-muted-foreground text-xs">—</span>
                            )}
                          </span>
                        </div>

                        <div className="md:col-span-2">
                          <span className="text-xs text-muted-foreground md:hidden">Character: </span>
                          <span className="font-extrabold text-primary text-xs uppercase tracking-widest">{line?.character || 'N/A'}</span>
                        </div>

                        <div className="md:col-span-2">
                          <p className="text-base leading-8 tracking-normal text-card-foreground truncate" title={line?.dialogue}>
                            {line?.dialogue}
                          </p>
                        </div>

                        <div className="md:col-span-1">
                          <div className="flex flex-wrap gap-1">
                            {line?.hasAudio && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-success/10 text-success text-xs">
                                <Icon name="Volume2" size={12} />
                              </span>
                            )}
                            {line?.glossaryTermCount > 0 && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-secondary/10 text-secondary text-xs">
                                <Icon name="BookOpen" size={12} />
                                {line?.glossaryTermCount}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="md:col-span-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditLine(line?.id)}
                            iconName="Edit"
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                  {filteredLines?.length === 0 && (
                    <div className="text-center py-12">
                      <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-card-foreground mb-2">
                        No lines found
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Try adjusting your search or filter criteria
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Batch Update Modal */}
      <BatchUpdateModal
        isOpen={isBatchModalOpen}
        onClose={() => setIsBatchModalOpen(false)}
        selectedLineIds={selectedLines}
        playId={playId}
        onUpdateComplete={handleBatchUpdateComplete}
      />

      {/* Batch Audio Upload Modal */}
      <BatchAudioUploadModal
        isOpen={isBatchAudioModalOpen}
        onClose={() => setIsBatchAudioModalOpen(false)}
        selectedLineIds={selectedLines}
        onUploadComplete={handleBatchAudioUploadComplete}
      />
    </div>
  );
};

export default EditPlay;